import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import type { ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { signalrService, type ConnectionState, type ReservationNotificationPayload } from '../services/signalrService';

interface RealtimeContextType {
  connectionState: ConnectionState;
  isConnected: boolean;
  joinFacilityGroup: (facilityId: string) => Promise<void>;
  leaveFacilityGroup: (facilityId: string) => Promise<void>;
  joinBusinessGroup: (businessProfileId: string) => Promise<void>;
  leaveBusinessGroup: (businessProfileId: string) => Promise<void>;
  onReservationCreated: (callback: (payload: ReservationNotificationPayload) => void) => () => void;
  onReservationCancelled: (callback: (payload: ReservationNotificationPayload) => void) => () => void;
  onReservationUpdated: (callback: (payload: ReservationNotificationPayload) => void) => () => void;
}

const RealtimeContext = createContext<RealtimeContextType | undefined>(undefined);

export const RealtimeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { token, isAuthenticated } = useAuth();
  const [connectionState, setConnectionState] = useState<ConnectionState>('disconnected');
  const hasConnectedRef = useRef(false);

  // Connect to SignalR when authenticated
  useEffect(() => {
    let cleanup: (() => void) | undefined;

    const connectToSignalR = async () => {
      if (!token || !isAuthenticated) {
        // Disconnect if not authenticated
        if (hasConnectedRef.current) {
          console.log('[RealtimeContext] Disconnecting: not authenticated');
          await signalrService.disconnect();
          hasConnectedRef.current = false;
        }
        return;
      }

      try {
        console.log('[RealtimeContext] Connecting to SignalR...');
        await signalrService.connect(token);
        hasConnectedRef.current = true;
      } catch (error) {
        console.error('[RealtimeContext] Failed to connect to SignalR:', error);
      }
    };

    // Subscribe to connection state changes
    cleanup = signalrService.onStateChange(setConnectionState);

    connectToSignalR();

    return () => {
      cleanup?.();
    };
  }, [token, isAuthenticated]);

  // Disconnect when component unmounts
  useEffect(() => {
    return () => {
      if (hasConnectedRef.current) {
        signalrService.disconnect();
        hasConnectedRef.current = false;
      }
    };
  }, []);

  const joinFacilityGroup = useCallback(async (facilityId: string) => {
    await signalrService.joinFacilityGroup(facilityId);
  }, []);

  const leaveFacilityGroup = useCallback(async (facilityId: string) => {
    await signalrService.leaveFacilityGroup(facilityId);
  }, []);

  const joinBusinessGroup = useCallback(async (businessProfileId: string) => {
    await signalrService.joinBusinessGroup(businessProfileId);
  }, []);

  const leaveBusinessGroup = useCallback(async (businessProfileId: string) => {
    await signalrService.leaveBusinessGroup(businessProfileId);
  }, []);

  const onReservationCreated = useCallback((callback: (payload: ReservationNotificationPayload) => void) => {
    return signalrService.onReservationCreated(callback);
  }, []);

  const onReservationCancelled = useCallback((callback: (payload: ReservationNotificationPayload) => void) => {
    return signalrService.onReservationCancelled(callback);
  }, []);

  const onReservationUpdated = useCallback((callback: (payload: ReservationNotificationPayload) => void) => {
    return signalrService.onReservationUpdated(callback);
  }, []);

  const value: RealtimeContextType = {
    connectionState,
    isConnected: connectionState === 'connected',
    joinFacilityGroup,
    leaveFacilityGroup,
    joinBusinessGroup,
    leaveBusinessGroup,
    onReservationCreated,
    onReservationCancelled,
    onReservationUpdated,
  };

  return (
    <RealtimeContext.Provider value={value}>
      {children}
    </RealtimeContext.Provider>
  );
};

export const useRealtime = (): RealtimeContextType => {
  const context = useContext(RealtimeContext);
  if (context === undefined) {
    throw new Error('useRealtime must be used within a RealtimeProvider');
  }
  return context;
};

export type { RealtimeContextType, ReservationNotificationPayload, ConnectionState };
